import { captureRemixErrorBoundaryError } from '@sentry/remix';
import type { LinksFunction } from 'react-router';
import posthog from 'posthog-js';
import superjson from 'superjson';

import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
  useNavigation,
} from 'react-router';

import { TooltipProvider } from './components/ui/tooltip';
import { Button } from './components/ui/button';

// Required CSS import, unless you're overriding the styling
import '@knocklabs/react/dist/index.css';

import styles from './globals.css?url';
import { currentUser } from './utils/auth';
import { Toaster } from './components/ui/toaster';
import { CurrentUserProvider } from './contexts/CurrentUserContext';
import { IssueNavigationProvider } from './contexts/IssueNavigationContext';
import { useEffect, useState, useRef } from 'react';
import { UpdateRouteWatcher } from './routes/update';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { AppRouter } from 'types/trpc';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TRPCProvider } from 'utils/trpc-client-helper';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'icon', href: '/favicon.ico' },
];

export async function loader({ request }: { request: Request }) {
  const user = await currentUser(request);

  return {
    user,
    isDevelopment: process.env.NODE_ENV === 'development',
  };
}

export const meta = () => [
  {
    charset: 'utf-8',
    title: `${
      process.env.NODE_ENV === 'development' ? '[DEV] ' : ''
    }Sunset | Admin`,
    viewport: 'width=device-width,initial-scale=1',
  },
];

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
    },
  });
}
let browserQueryClient: QueryClient | undefined = undefined;
function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

function App() {
  const { user } = useLoaderData<typeof loader>();
  const navigation = useNavigation();
  const [showWarning, setShowWarning] = useState<boolean>(true);
  const timerRef = useRef<number | null>(null);

  const apiUrl = import.meta.env.VITE_NODE_API_URL;
  // Direct connection to Node API tRPC endpoint
  const url = `${apiUrl}/trpc`;
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        httpBatchLink({
          url: url,
          transformer: superjson,
          fetch(url, options) {
            return fetch(url, {
              ...options,
              credentials: 'include',
            });
          },
        }),
      ],
    })
  );

  useEffect(() => {
    try {
      if (user) {
        posthog.identify(user.user_id, { email: user.email, name: user.name });
      }
    } catch (error) {
      console.error('Error identifying user:', error);
    }
  }, [user]);

  // Clear any existing timer when component unmounts
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleDismiss = () => {
    setShowWarning(false);

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set a new timer to show the warning again after 30 seconds
    timerRef.current = window.setTimeout(() => {
      setShowWarning(true);
      timerRef.current = null;
    }, 30000);
  };

  return (
    <html>
      <head>
        <link rel="icon" href="data:image/x-icon;base64,AA" />
        <Meta />
        <Links />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            <CurrentUserProvider
              value={user ? { ...user, id: user.user_id } : null}
            >
              <IssueNavigationProvider>
                <TooltipProvider>
                  <main className="flex flex-col h-screen">
                    {navigation.state !== 'idle' && (
                      <div className="fixed top-0 left-0 right-0 z-50">
                        <div className="h-[2px] w-full bg-gray-100/20">
                          <div className="h-full w-[20%] bg-blue-400 animate-loader will-change-[width] origin-left" />
                        </div>
                      </div>
                    )}
                    {showWarning && (
                      <div
                        id="production-warning"
                        className="hidden h-[100vh] w-[100vw] border-[8px] border-yellow-500 fixed top-0 left-0 z-[1000] pointer-events-none"
                      >
                        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                          <div className="h-full w-[100%] bg-yellow-500/20 animate-sweep will-change-transform pointer-events-none" />
                        </div>
                        <div className="text-center text-white bg-yellow-500 p-2 rounded-b-lg fixed top-0 left-1/2 -translate-x-1/2 z-[1000] flex items-center justify-center">
                          WARNING: You are viewing the production environment.
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-2 bg-yellow-600 hover:bg-yellow-700 text-white pointer-events-auto"
                            onClick={handleDismiss}
                          >
                            Dismiss for 30s
                          </Button>
                        </div>
                      </div>
                    )}
                    <UpdateRouteWatcher />
                    <Outlet />
                  </main>
                  <ScrollRestoration />
                  <Scripts />
                  <Toaster />
                </TooltipProvider>
              </IssueNavigationProvider>
            </CurrentUserProvider>
          </TRPCProvider>
        </QueryClientProvider>
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  if (
    process.env.NODE_ENV !== 'development' &&
    process.env.NODE_ENV !== 'test'
  ) {
    const error = useRouteError();
    captureRemixErrorBoundaryError(error);
    return (
      <html>
        <head>
          <title>Oh no!</title>
          <Meta />
          <Links />
        </head>
        <body>
          <div className="flex h-[100dvh] flex-col items-center justify-center bg-background-tertiary px-4 dark:bg-gray-900">
            <div className="mx-auto max-w-md text-center">
              <h1 className="mb-2 text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100 sm:text-5xl">
                We apologize for the inconvenience.
              </h1>
              <p className="mb-6 text-gray-600 dark:text-text-tertiary">
                The page you're attempting to access is currently unavailable.
                Our team has been notified and will try to resolve the issue as
                soon as possible.
              </p>
              <a
                className="inline-flex h-10 items-center justify-center rounded-md bg-gray-900 px-6 text-sm font-medium text-gray-50 shadow transition-colors hover:bg-gray-900/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50 dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-50/90 dark:focus-visible:ring-gray-300"
                href="/"
              >
                Take me back home
              </a>
              <div className="mt-8 rounded-md bg-yellow-100 p-4 dark:bg-yellow-800">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  You can always reach out to your Account Manager with any
                  questions in the meantime.
                </p>
              </div>
            </div>
          </div>
          <Scripts />
        </body>
      </html>
    );
  } else {
    const error = useRouteError();

    if (isRouteErrorResponse(error)) {
      return (
        <div>
          <h1>
            {error.status} {error.statusText}
          </h1>
          <p>{error.data}</p>
        </div>
      );
    } else if (error instanceof Error) {
      return (
        <div>
          <h1>Error</h1>
          <p>{error.message}</p>
          <p>The stack trace is:</p>
          <pre>{error.stack}</pre>
        </div>
      );
    } else {
      return <h1>Unknown Error</h1>;
    }
  }
};
export default App;
